// src/utils/table-export.ts
/**
 * Dependency-free table exports. One column definition drives both formats.
 *
 * - `downloadCsv`  — RFC 4180 CSV. Opens everywhere; the default.
 * - `downloadXlsx` — real OOXML .xlsx package (see ./xlsx). Opens in Excel,
 *                    Apple Numbers, Google Sheets and LibreOffice.
 *
 * Both escape their input: CSV values are quoted and defused against formula
 * injection, XLSX values are XML-escaped.
 */

import { buildXlsxBytes } from "./xlsx";

export type TableCellValue = string | number | boolean | null | undefined;

export type TableColumn<T> = {
	/** Header text shown in the first row. */
	header: string;
	/** Cell value for a given row. `null`/`undefined` render as empty. */
	value: (row: T) => TableCellValue;
};

/* -------------------------------------------------------------------------- */
/* shared                                                                     */
/* -------------------------------------------------------------------------- */

const toText = (value: TableCellValue): string => {
	if (value === null || value === undefined) return "";
	if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
	return String(value);
};

/** Keeps the download from writing anything odd to the user's filesystem. */
const sanitizeFileName = (name: string): string =>
	name.replace(/[\\/:*?"<>|]/g, "-").replace(/\s+/g, " ").trim() || "export";

const triggerDownload = (blob: Blob, fileName: string): void => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	link.style.display = "none";
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

/* -------------------------------------------------------------------------- */
/* CSV                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Spreadsheet apps execute a cell that starts with one of these, so a value
 * like `=HYPERLINK(...)` coming from user-entered data would run on open.
 * Prefixing with an apostrophe forces it to be treated as text.
 */
const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

const escapeCsv = (value: TableCellValue): string => {
	// Emit plain numbers unquoted so they land as numeric cells, not text.
	if (typeof value === "number" && Number.isFinite(value)) return String(value);

	let text = toText(value);
	if (text === "") return "";
	if (FORMULA_TRIGGERS.includes(text[0])) {
		text = `'${text}`;
	}
	return `"${text.replace(/"/g, '""')}"`;
};

export function buildCsv<T>(
	rows: readonly T[],
	columns: readonly TableColumn<T>[],
): string {
	return [
		columns.map((c) => escapeCsv(c.header)).join(","),
		...rows.map((row) => columns.map((c) => escapeCsv(c.value(row))).join(",")),
	].join("\r\n");
}

/** Builds a CSV from `rows` and triggers a browser download. */
export function downloadCsv<T>(
	fileName: string,
	rows: readonly T[],
	columns: readonly TableColumn<T>[],
): void {
	// BOM so Excel detects UTF-8 (Hebrew names, accented characters, etc.).
	const blob = new Blob(["﻿", buildCsv(rows, columns)], {
		type: "text/csv;charset=utf-8",
	});
	triggerDownload(blob, `${sanitizeFileName(fileName)}.csv`);
}

/* -------------------------------------------------------------------------- */
/* XLSX                                                                       */
/* -------------------------------------------------------------------------- */

/** Builds an .xlsx workbook from `rows` and triggers a browser download. */
export function downloadXlsx<T>(
	fileName: string,
	rows: readonly T[],
	columns: readonly TableColumn<T>[],
	sheetName = "Sheet1",
): void {
	const bytes = buildXlsxBytes(rows, columns, sheetName);
	const blob = new Blob([bytes], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	triggerDownload(blob, `${sanitizeFileName(fileName)}.xlsx`);
}
